import className from "classnames/bind";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { MouseEventHandler, useEffect, useState } from "react";

import { SIZES as CHIP_SIZES } from "@/shared/constants";

import type { UI_CONTEXT } from "@/analytics/constants";
import { UI_CONTEXTS } from "@/analytics/constants";
import EVENTS from "@/analytics/events";
import { trackUserEvent } from "@/analytics/tracking";

import {
    KINDS as BUTTON_KINDS,
    SIZES as BUTTON_SIZES,
} from "@/components/buttons/buttons.constants";
import LinkButton from "@/components/buttons/link-button";
import Chip from "@/components/chips/chip";
import { KINDS as CHIP_KINDS } from "@/components/chips/chips.constants";
import { DropdownMenu } from "@/components/dropdown-menus/dropdown-menu";
import { DropdownMenuItem } from "@/components/dropdown-menus/dropdown-menu-items/dropdown-menu-item-interface";
import { KINDS as DROPDOWN_KINDS } from "@/components/dropdown-menus/dropdown-menu.constants";

import { useFeatureFlags } from "@/hooks/useFeatureFlags";

import { CategoryProps } from "@/interfaces/category-interface";

import { Locale } from "@/types/locale";

import CloseIcon from "../../assets/icons/close-icon.svg";
import InStore from "../../assets/icons/in-store-icon.svg";
import styles from "./controlFilters.module.scss";

const cx = className.bind(styles);

export interface Props {
    locale: Locale;
    currentCategory: CategoryProps;
    hasOffers: boolean;
    hasInStore: boolean;
    handleOfferClick: () => void;
    handleInStoreClick: () => void;
    handleSortByClick: (sortByValue: string) => void;
    sortByOptions: Array<DropdownMenuItem>;
    inStoreLocationUrl: string;
    uiContext: UI_CONTEXT;
}

export const ControlFilters: React.FC<Props> = (props) => {
    const {
        currentCategory,
        locale,
        hasOffers,
        hasInStore,
        handleOfferClick,
        handleInStoreClick,
        handleSortByClick,
        sortByOptions,
        inStoreLocationUrl,
        uiContext,
    } = props;

    const { t } = useTranslation();
    const { inStoreDisabled } = useFeatureFlags();

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortByFilterName, setSortByFilterName] = useState(sortByOptions[0].name);
    const router = useRouter();

    useEffect(() => {
        const sortBy = router.query.sort_by;
        if (!sortBy) {
            return;
        }
        const option = sortByOptions.find((option) => option.value === sortBy);
        if (option) {
            setSortByFilterName(option.name);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const chipsProps = {
        disabled: false,
        size: CHIP_SIZES.REGULAR,
    };

    const desktopFilterContainerClassName = cx({
        [styles.desktopFilterContainer]: true,
        [styles.desktopFilterContainerActive]: isFilterOpen,
    });

    const mobileFilterContainerClassName = cx({
        [styles.mobileFilterContainer]: true,
        [styles.mobileFilterContainerActive]: isFilterOpen,
    });

    const filterOverlayClassName = cx({
        [styles.filterOverlayActive]: isFilterOpen,
    });

    const handleSortByClickWithTracking = (item: DropdownMenuItem) => {
        trackEvent(EVENTS.SORT_CLICK, item.name);
        setSortByFilterName(item.name);
        setIsFilterOpen(!isFilterOpen);
        handleSortByClick(item.value);
    };

    const trackEvent = (eventName: string, sort?: string) => {
        const eventProps = {
            sort,
            categoryName: currentCategory.name,
            categoryId: currentCategory.id,
        };
        trackUserEvent(locale, router.asPath, uiContext, eventName, eventProps);
    };

    const handleClick: MouseEventHandler = () => {
        setIsFilterOpen(!isFilterOpen);
    };

    const handleOffersClickWithTracking: MouseEventHandler = () => {
        trackEvent(EVENTS.FILTER_OFFER_CLICK);
        handleOfferClick();
    };

    const handleInStoreClickWithTracking: MouseEventHandler = () => {
        trackEvent(EVENTS.FILTER_INSTORE_CLICK);
        handleInStoreClick();
    };

    return (
        <div data-testid="control-filters" className={styles.controlHeaderFiltersContainer}>
            <div className={styles.filtersContainer}>
                {currentCategory && (
                    <Chip
                        {...chipsProps}
                        label={t("store.filterOffersOptionText")}
                        kind={CHIP_KINDS.SINGLE}
                        handleClick={handleOffersClickWithTracking}
                        isActive={hasOffers}
                        testId="offers-chip"
                    />
                )}
                {currentCategory && !inStoreDisabled && (
                    <Chip
                        {...chipsProps}
                        label={t("store.filterInStoreOptionText")}
                        kind={CHIP_KINDS.SINGLE}
                        handleClick={handleInStoreClickWithTracking}
                        isActive={hasInStore}
                        testId="in-store-chip"
                    />
                )}
                {currentCategory && (
                    <div>
                        <Chip
                            {...chipsProps}
                            label={`${t("store.filterSortByText")}: ${sortByFilterName}`}
                            kind={CHIP_KINDS.DROPDOWN}
                            handleClick={handleClick}
                            isFilterOpen={isFilterOpen}
                        />
                        {/* Desktop filter */}
                        <div className={desktopFilterContainerClassName}>
                            <DropdownMenu
                                dropdownMenuItems={sortByOptions}
                                kind={DROPDOWN_KINDS.TEXT}
                                onClick={handleSortByClickWithTracking}
                            />
                        </div>
                        {/* Mobile filter */}
                        <div className={mobileFilterContainerClassName}>
                            <span className={styles.mobileFilterHeader}>Sort results</span>
                            <span className={styles.mobileFiltersCloseIcon} onClick={handleClick}>
                                <CloseIcon />
                            </span>
                            <DropdownMenu
                                dropdownMenuItems={sortByOptions}
                                kind={DROPDOWN_KINDS.TEXT}
                                withBorder={false}
                                onClick={handleSortByClickWithTracking}
                            />
                        </div>
                        <div className={filterOverlayClassName} onClick={handleClick} />
                    </div>
                )}
            </div>
            {currentCategory && !inStoreDisabled && (
                <LinkButton
                    kind={BUTTON_KINDS.CHIP}
                    size={BUTTON_SIZES.UNSET}
                    url={inStoreLocationUrl}
                    prefetch={false}
                    label={t("category.inStoreMap")}
                    overrideFocusBorder={false}
                    analytics={{
                        uiContext: UI_CONTEXTS.CATEGORY_DIRECTORY_FILTER,
                        eventProps: {
                            categoryId: currentCategory.id,
                            categoryName: currentCategory.name,
                        },
                        eventName: EVENTS.SHOP_DIRECTORY_CATEGORY_LIST_INSTORE_CLICK,
                    }}
                    labelElement={<InStore />}
                />
            )}
        </div>
    );
};
